import { useEffect, useState } from "react";
import api, { fmtDate, formatApiError, openFilePreview } from "@/lib/apiClient";
import EmployeeLayout from "@/layouts/EmployeeLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Paperclip, FileText } from "lucide-react";
import { toast } from "sonner";

const BLANK = { first_day: "", expected_return: "", reason_category: "Flu", notes: "", cert_file: null, cert_name: "" };

export default function ReportSickness() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const load = () => api.get("/sick-leave").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, cert_file: reader.result, cert_name: f.name }));
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!form.first_day) { toast.error("Enter first day absent"); return; }
    try { await api.post("/sick-leave", form); toast.success("Sick leave reported"); setOpen(false); setForm(BLANK); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const viewCert = (r) => { if (!openFilePreview(r.cert_file, r.cert_name || "cert")) toast.error("Unable to open this file"); };

  return (
    <EmployeeLayout title="Sickness">
      <Button data-testid="report-sick-btn" onClick={() => { setForm(BLANK); setOpen(true); }} className="w-full h-12 bg-amber-500 text-black font-bold hover:bg-amber-600 mb-4">Report sickness</Button>
      <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">My sick leave</h2>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-sm text-zinc-400">No records.</div>}
        {rows.map((r) => (
          <div key={r.id} className="bg-white border border-zinc-200 rounded-md p-3">
            <div className="flex justify-between"><span className="font-medium text-sm">{r.reason_category}</span>{r.certified ? <StatusBadge status="approved" /> : <StatusBadge status="pending" />}</div>
            <div className="num text-xs text-zinc-500 mt-1">From {fmtDate(r.first_day)}{r.actual_return ? ` · returned ${fmtDate(r.actual_return)}` : ""}</div>
            {r.cert_file && <button data-testid={`view-cert-${r.id}`} onClick={() => viewCert(r)} className="mt-2 inline-flex items-center gap-1 text-xs text-amber-600 font-semibold"><FileText className="h-3.5 w-3.5" /> View sick cert</button>}
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Report sickness</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>First day absent</Label><Input data-testid="sick-first" type="date" value={form.first_day} onChange={(e) => setForm({ ...form, first_day: e.target.value })} className="h-12 mt-1" /></div>
            <div><Label>Expected return</Label><Input data-testid="sick-return" type="date" value={form.expected_return} onChange={(e) => setForm({ ...form, expected_return: e.target.value })} className="h-12 mt-1" /></div>
            <div>
              <Label>Reason</Label>
              <Select value={form.reason_category} onValueChange={(v) => setForm({ ...form, reason_category: v })}>
                <SelectTrigger data-testid="sick-reason" className="h-12 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{["Flu", "Injury", "Medical", "Stress", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sick cert (photo or PDF)</Label>
              <label data-testid="sick-cert-label" className="mt-1 flex items-center gap-2 h-12 px-3 border border-dashed border-zinc-300 rounded-md cursor-pointer text-sm text-zinc-500 hover:border-amber-400">
                <Paperclip className="h-4 w-4" />
                <span className="truncate">{form.cert_name || "Upload sick cert"}</span>
                <input data-testid="sick-cert" type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" />
              </label>
            </div>
            <div><Label>Notes</Label><Textarea data-testid="sick-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <Button data-testid="sick-submit" onClick={submit} className="w-full h-12 bg-slate-900 text-white">Report</Button>
          </div>
        </DialogContent>
      </Dialog>
    </EmployeeLayout>
  );
}
